import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { preloadLanguage } from "@/lib/i18n";

/**
 * Keeps i18next language in sync with the URL prefix:
 * - URLs starting with /fr → French
 * - All other URLs → English
 *
 * On the very first visit (no localStorage preference), if the browser
 * language is French and the user landed on /, redirect to /fr.
 *
 * Also updates <html lang="..."> and persists the choice to localStorage.
 */
export default function useLanguageSync() {
  const { i18n } = useTranslation();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const didAutoRedirect = useRef(false);

  // Auto-redirect French browsers from / → /fr on first visit only.
  useEffect(() => {
    if (didAutoRedirect.current) return;
    didAutoRedirect.current = true;

    const stored = typeof window !== "undefined"
      ? window.localStorage.getItem("docuchase-lang")
      : null;
    if (stored) return; // user has chosen — respect it

    const browserLang = (typeof navigator !== "undefined" ? navigator.language : "") || "";
    const prefersFr = browserLang.toLowerCase().startsWith("fr");

    // Only redirect from the marketing root, never from app/portal/auth routes
    if (prefersFr && pathname === "/") {
      navigate("/fr" + search + hash, { replace: true });
    }
    // We deliberately run only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL → language sync.
  // IMPORTANT: preload the target language's namespaces BEFORE flipping
  // i18n.language, otherwise components re-render against an empty bundle
  // and briefly show raw keys / blank badges while the JSON chunk loads.
  useEffect(() => {
    const isFr = pathname === "/fr" || pathname.startsWith("/fr/");
    const target = isFr ? "fr" : "en";
    if (typeof document !== "undefined") {
      document.documentElement.lang = target;
    }
    if (i18n.language === target) return;

    let cancelled = false;
    // Always make sure landing + common are present for the target language
    // before switching. Other namespaces (auth/dashboard/...) are loaded
    // on demand and gated by their own routes.
    void preloadLanguage(target, ["common", "landing"]).then(() => {
      if (cancelled) return;
      void i18n.changeLanguage(target);
    });
    return () => {
      cancelled = true;
    };
  }, [pathname, i18n]);
}
